import { useSnackbar } from '@e2m-customer-portal/hooks';
import { InputFieldType } from '@e2m-customer-portal/shared';
import { Button, CircularProgress } from '@mui/material';
import { addDays, Interval } from 'date-fns';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyIcon } from '../../assets/icons';
import { useForecastDataMutation } from '../../domains/forecast/short-term-forecast/forecast-list/forecast-table/queries';
import { Device, ForecastSlice, ForecastSliceInput } from '../../generated/graphql';

interface CopyPasteForecastButtonProps {
  device: Device;
  interval: Interval;
  slices: readonly ForecastSlice[];
  disabled?: boolean;
}

export function CopyPasteForecastButton({
  device,
  interval,
  slices,
  disabled,
}: Readonly<CopyPasteForecastButtonProps>): ReactElement {
  const [t] = useTranslation();
  const { showSuccess, showError, showInfo } = useSnackbar();

  const nextInterval: Interval = {
    start: interval.end,
    end: addDays(interval.end, 7),
  };

  const [update, { loading }] = useForecastDataMutation();

  const slicesInput: ForecastSliceInput[] = slices
    .filter((slice) => !slice.hasDowntime)
    .map((slice) => ({
      date: addDays(slice.date, 7),
      fields: [
        {
          type: InputFieldType.PRODUCTION,
          watt: slice.fields.find((value) => value.type === InputFieldType.PRODUCTION)?.watt ?? 0,
        },
      ],
      hasDowntime: slice.hasDowntime,
    }));

  const downtimesInput: ForecastSliceInput[] = slices
    .filter((slice) => slice.hasDowntime)
    .map((slice) => ({
      date: addDays(slice.date, 7),
      fields: [
        {
          type: InputFieldType.PRODUCTION,
          watt: slice.fields.find((value) => value.type === InputFieldType.PRODUCTION)?.watt ?? 0,
        },
      ],
      hasDowntime: slice.hasDowntime,
    }));

  return (
    <Button
      className="w-fit justify-center border-green-200 text-xs disabled:border-grey-200 sm:text-sm"
      color="primary"
      data-testid="CopyPasteForecastButton"
      variant="outlined"
      disabled={disabled || loading}
      onClick={async () => {
        try {
          if (slicesInput.length) {
            await update(device.id, nextInterval, slicesInput);
            showSuccess(t('FORECAST_COPY_PASTE_CONFIRM_MESSAGE'));
          }
          if (downtimesInput.length) {
            await update(device.id, nextInterval, downtimesInput);
            setTimeout(() => showInfo(t('FORECAST_COPY_PASTE_HAS_DOWNTIME_MESSAGE')), 2000);
          }
        } catch {
          showError(t('FORECAST_COPY_PASTE_ERROR_MESSAGE'));
        }
      }}
    >
      {loading ? <CircularProgress className="min-w-5" size="" color="inherit" /> : <CopyIcon className="min-w-5" />}
      <span className="ml-1 hidden sm:inline">{t('FORECAST_COPY_PASTE_BUTTON')}</span>
    </Button>
  );
}
